import React, { useContext, useRef, useState } from 'react'
import { ProductLocationContext } from './ProductLocationContext'
import styled from 'styled-components'
import ProductLocationModal from '../components/ProductLocationModal'
import { useDetectOutsideClick } from '../../../../../../generic/hooks/useDetectOutsideClick'
import { IPalletData } from '../types/productLocation'
import { IResponseSrikaNode, ISrikaItem } from '../../../../../../modules/API/Gathering/models/srika'
import { Gathering } from '../../../../../../modules/API/Gathering/gathering'
import { handleNotifications } from '../../../../../../generic/utils/handleNotifications'
import { useDispatch } from 'react-redux'
import { LOAD, LOAD_END } from '../../../../../../../store/actions/actionsTypes'
import { Warehouse } from '../../../../../../modules/API/Warehouse/warehouse'
import { useBarcodeScanner } from '../../../../../../generic/hooks/useBarcodeScanner'
import { useWarehouseLocations, findLocationByCode } from '../../../../../../generic/hooks/useWarehouseLocations'

type Props = {
  children: React.ReactNode | React.ReactNode[]
}

const ProductLocationProvider = ({
  children
}: Props) => {
  const dispatch = useDispatch()
  const { locations } = useWarehouseLocations()
  const [scannedPallet, setScannedPallet] = useState<ISrikaItem | null>(null)
  const [scannedLocation, setScannedLocation] = useState<IResponseSrikaNode | null>(null)
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false)
  const modalRef = useRef<HTMLDivElement>(null)
  const [barecode, setBarecode] = useState<string>("")
  const [locationCode, setLocationCode] = useState<string>("")
  const handleScanPallet = async (pallet: string) => {
    try {
      const res = await Gathering.getKnisaByBarcode(pallet)
      setScannedPallet(res.entryLine[0])
    } catch (err) {
      handleNotifications('לא נמצא פריט עם קוד זה', { type: "warning" })
    }
  }
  const handleBoundPalletToLocation = async () => {
    dispatch({type: LOAD})
    try {
      const code = barecode.split('-')
      if(code.length !== 2) {
        handleNotifications('קוד כניסה לא תקין', { type: "error" })
        return
      }
       await Warehouse.boundPalletToLocation({
        DetailNum: Number(code[1]),
        EntryNum: Number(code[0]),
        Locating: locationCode
      })
      setScannedPallet(null)
      setBarecode("")
      setScannedLocation(null)
      setLocationCode("")
      console.log('איתור נוסף בהצלחה')
      handleNotifications('איתור נוסף בהצלחה', { type: "success" })
    } catch (err) {
      handleNotifications('משהו השתבש', { type: "warning" })
    } finally {
      dispatch({type: LOAD_END})
    }
  }
  const handleScanLocation = async (location: string) => {
    if(!scannedPallet) return
    if (!findLocationByCode(locations, location)) {
      handleNotifications('איתור לא נמצא במערכת', { type: "warning" })
      return
    }
    dispatch({type: LOAD})
    try {
      const res = await Warehouse.getEntryDetailsByLocation(location.trim())
      setScannedLocation(res)
    } catch (err) {
      handleNotifications('משהו השתבש', { type: "warning" })
    } finally {
      dispatch({type: LOAD_END})
    }
  }

  useBarcodeScanner({
    onScan: (value) => {
      if (!scannedPallet) {
        setBarecode(value)
        handleScanPallet(value)
      } else {
        setLocationCode(value)
        handleScanLocation(value)
      }
    },
    duplicateScanWindowMs: 700,
  })

  const openModal = () => {
    setIsOpenModal(true)
  }

  const closeModal = () => {
    setIsOpenModal(false)
  }

  const clearScannedDataPallet = () => {
    setScannedPallet(null)
    setBarecode("")
  }
  const clearScannedDataLocation = () => {
    setScannedLocation(null)
    setLocationCode("")
  }
  useDetectOutsideClick(modalRef, closeModal)
  return (
    <ProductLocationContext.Provider value={{
      scannedPallet,
      scannedLocation,
      isOpenModal,
      barecode,
      setBarecode,
      locationCode,
      setLocationCode,
      openModal,
      closeModal,
      handleScanPallet,
      handleScanLocation,
      clearScannedDataPallet,
      clearScannedDataLocation,
      handleBoundPalletToLocation
    }}>
      {children}
      {isOpenModal && (
        <Overlay>
          <ModalWrapper ref={modalRef}>
            <ProductLocationModal />
          </ModalWrapper>
        </Overlay>
      )}
    </ProductLocationContext.Provider>
  )
}

export const useProductLocation = () => {
  const context = useContext(ProductLocationContext)
  if (!context) {
    throw new Error('useProductLocation must be used within a ProductLocationProvider')
  }
  return context
}
export default ProductLocationProvider

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`

const ModalWrapper = styled.div`
  background: white;
  padding: 1em;
  border-radius: 8px;
  max-height: 90vh;
  overflow-y: auto;
`